// PatientOutcomeProgress.tsx
import { Col, Flex, Progress, Row, Skeleton, Spin, Typography } from 'antd';
import { BaseHeader } from 'components/header';
import { useReducer } from 'lib/hooks/useReducer';
import { useEffect } from 'react';

import {
  initialState,
  QuestionnaireControlSliceReducer,
  questionnaireInfoInitialLoad
} from 'features/patient/assessment/assessment-controller/questionnaire-control-flow-slice';

const { Title } = Typography;

import { BaseButton } from 'components/button/button-base';
import { Collection } from 'lib/documents/document';
import { useAppSelector } from 'lib/store/rootReducer';
import moment from 'moment';
import { getPatientLinkUrl } from 'navigation/router/route-switch/route-switch-patient/utils/patient-route-helpers';
import { Link, useParams } from 'react-router-dom';
import { PatientRoutes } from 'routes';
import { useControlFlowState } from './assessment-controller/_utils/use-controlFlowState';
import './assessment-progress.scss';
import { QuestionnaireControlFlowState } from './assessment-controller/questionnaire-control-flow-slice-types';
import { AssessmentComplete } from './assessment-complete';
import { useGetPendingAssessmentData } from 'lib/hooks/tanstack/use-getPendingAssessmentData';
import { ErrorDisplay } from 'components/ErrorDisplay';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import { CRX_SUPPORT_EMAIL } from 'app/config';

export function PatientOutcomeProgress() {
  const { patientId } = useParams<{ patientId: string }>();
  const controlFlowState = useControlFlowState(patientId);
  const patientInfo = useGetPendingAssessmentData(patientId);

  if ((controlFlowState.isLoading, patientInfo.isLoading)) {
    return <Skeleton active />;
  }

  if (patientInfo.isError) {
    return (
      <ErrorDisplay
        error={resolveError(patientInfo.error)}
        title={'Something went wrong when fetching Patient assessment data'}
        description={`Please report this bug to ${CRX_SUPPORT_EMAIL}.`}
      />
    );
  }

  if (controlFlowState.allDone) {
    return (
      <AssessmentComplete
        overrideAssessmentType={patientInfo.data?.overview?.assessmentType}
      />
    );
  }

  return (
    <div className="outcome-progress-container">
      <BaseHeader />
      <div className="outcome-content-container">
        <ContinueProgress
          controlFlowState={controlFlowState}
          patientId={patientId}
        />
      </div>
    </div>
  );
}

// function Assessmentcomplete() {
//   return (
//     <Row className="title-container">
//       <Col className="font-brand" style={{ marginBottom: '35px' }}>
//         <Row>
//           <h1 className="crx-typography h1" style={{ marginBottom: '8px' }}>
//             <CheckOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
//             All done!
//           </h1>
//         </Row>
//         <Row>
//           <Typography className="crx-typography body-normal">
//             Your assessment is currently being processed. You can safely log out
//             or close the page.
//           </Typography>
//         </Row>
//       </Col>
//     </Row>
//   );
// }

function ContinueProgress(props: {
  controlFlowState: QuestionnaireControlFlowState;
  patientId: string;
}) {
  const { required, fulfilled, assessmentData } = props.controlFlowState;

  const getPercent = () => Math.min(100, (fulfilled / required) * 100);
  const val = `${getPercent()}%`;
  const daysLeft = assessmentData?.expirationDate.diff(moment(), 'days') ?? 0;
  const continueLink = getPatientLinkUrl(
    PatientRoutes.AssessmentControlFlow,
    props.patientId
  );
  const heroText = fulfilled > 0 ? 'Good progress' : 'Get started!';
  const heroSubtitle =
    'You are one step closer to receiving your treatment recommendations';
  const actionButtonText =
    fulfilled > 0 ? 'Continue the assessment' : 'Start the assessment';

  return (
    <>
      <Row className="title-container">
        <Col className="font-brand" style={{ marginBottom: '35px' }}>
          <Row>
            <h1 className="crx-typography h1" style={{ marginBottom: '8px' }}>
              {heroText}
            </h1>
          </Row>
          <Row>
            <Typography className="crx-typography body-normal">
              {heroSubtitle}
            </Typography>
          </Row>
        </Col>
      </Row>
      <Row style={{ marginBottom: '60px' }}>
        <Col span={24}>
          <Row justify="space-between">
            <Col>
              {required === 0 ? (
                <Flex gap={'small'}>
                  <Title level={3} style={{ color: '#242425' }}>
                    Loading progress...
                  </Title>
                  <Spin />
                </Flex>
              ) : (
                <Title level={3} style={{ fontSize: '52px', color: '#242425' }}>
                  {val}
                  <span className="title-complete font-brand"> completed</span>
                </Title>
              )}
            </Col>
            <Col
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Row>
                <Typography.Title
                  level={3}
                  className="font-brand crx-typography h3"
                >
                  {required - fulfilled}/{required} measures left
                </Typography.Title>
              </Row>
            </Col>
          </Row>
          <Row>
            <ConnectedOutcomeMeasuresProgressBar />
          </Row>
        </Col>
      </Row>
      <Row style={{ marginBottom: '50px' }}>
        <Col>
          <Row>
            <Title level={3} className="crx-typography h3">
              Time remaining:
            </Title>
          </Row>
          <Row>
            <DisplayTimeRemaining daysLeft={daysLeft} />
          </Row>
        </Col>
      </Row>
      <Link to={continueLink}>
        <BaseButton
          className="crx-typography button-big"
          style={{ padding: '30px' }}
        >
          {actionButtonText}
        </BaseButton>
      </Link>
    </>
  );
}

function DisplayTimeRemaining(props: { daysLeft: number }) {
  return (
    <div className="days-left-row">
      <div className="days-left-container">
        <Title level={1}>{props.daysLeft}</Title>
        <div className="days-left-days-left">
          <h3 className="crx-typography h3" style={{ marginBottom: '0px' }}>
            Days
          </h3>
          <h3 className="crx-typography h3">left</h3>
        </div>
      </div>
      <div className="crx-typography body-normal days-left-text">
        before you have to restart the assessment to maintain test accuracy.
      </div>
    </div>
  );
}

export function ConnectedOutcomeMeasuresProgressBar(
  props: Omit<
    React.ComponentProps<typeof OutcomeMeasuresProgressBar>,
    'fulfilled' | 'required'
  >
) {
  const [{ required, fulfilled }, dispatch] = useReducer(
    QuestionnaireControlSliceReducer,
    initialState
  );

  const user = useAppSelector(state => state.auth.user);

  useEffect(() => {
    dispatch(
      questionnaireInfoInitialLoad({
        patientId: user!.uid,
        rootPath: Collection.PendingPatientAssessments
      })
    );
  }, [user]);

  return (
    <OutcomeMeasuresProgressBar
      fulfilled={fulfilled}
      required={required}
      {...props}
    />
  );
}

export function OutcomeMeasuresProgressBar(
  props: {
    fulfilled: number;
    required: number;
    label?: boolean;
  } & Omit<React.ComponentProps<typeof Progress>, 'percent'>
) {
  const { fulfilled, required, label, ...other } = props;
  const getPercent = () => Math.min(100, (fulfilled / required) * 100);
  const val = `${getPercent()}%`;

  return (
    <>
      {
        /**
         * Show a completion label
         */
        label && (
          <span style={{ display: 'flex', alignItems: 'baseline' }}>
            {required === 0 ? (
              <Flex gap={'small'}>
                <Title>Fetching progress...</Title>
                <Spin />
              </Flex>
            ) : (
              <>
                <Typography.Title style={{ marginRight: '0.5rem' }}>
                  {val}
                </Typography.Title>
                <Typography>completed</Typography>
              </>
            )}
          </span>
        )
      }
      <Progress
        percent={getPercent()}
        strokeColor={'rgba(163, 102, 252, 1)'}
        trailColor={'rgba(163, 102, 252, 0.25)'}
        showInfo={false}
        size={['100%', 40]}
        {...other}
      />
    </>
  );
}
